import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { auth } from '@/lib/firebase';
import { ArrowLeft, Building2, User, Phone, Mail, Calendar, Loader2, RefreshCw, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCompanyRequests } from '@/hooks/useCompanyRequests';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/context/AuthContext';

const CompanyRequests = () => {
  const { data: requests = [], isLoading: loading, refetch } = useCompanyRequests();
  const { user } = useAuth();
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const handleUpdateStatus = async (id: string, newStatus: string) => {
    setUpdatingId(id);
    try {
      const user = auth.currentUser;
      const token = user ? await user.getIdToken() : '';
      
      const response = await fetch('https://europe-west1-dagens-l-tt.cloudfunctions.net/updateCompanyRequestStatus', {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id, status: newStatus })
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      // Update cache optimistically
      const updatedData = queryClient.setQueryData(['companyRequests', user?.uid], (oldData: any) => {
        if (!oldData) return [];
        return oldData.map((req: any) => req.id === id ? { ...req, status: newStatus } : req);
      });

      // Uppdatera även localStorage så att notisen försvinner direkt vid sidladdning
      try {
        localStorage.setItem('cachedCompanyRequests', JSON.stringify(updatedData));
      } catch (e) {}
    } catch (error) {
      console.error("Fel vid uppdatering av status:", error);
    } finally {
      setUpdatingId(null);
    }
  };

  const formatDate = (isoString: string) => {
    if (!isoString) return 'Okänt datum';
    const date = new Date(isoString);
    return date.toLocaleDateString('sv-SE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background border-b border-border">
        <div className="container-wide mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft size={20} />
            </Link>
            <h1 className="font-display text-xl font-semibold text-foreground">
              Personalvy – Förfrågningar
            </h1>
            <Button variant="ghost" size="icon" onClick={() => refetch()} disabled={loading} className="text-muted-foreground">
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/personal/meny">
              <Button variant="ghost">Meny</Button>
            </Link>
            <Link to="/personal/kassa">
              <Button variant="secondary">Kassa</Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="container-wide mx-auto px-4 py-8 animate-fade-in-up">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
            <Loader2 className="h-8 w-8 animate-spin mb-4 text-primary" />
            <p>Laddar förfrågningar...</p>
          </div>
        ) : requests.length === 0 ? (
          <div className="card-warm p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center mx-auto mb-6">
              <Building2 size={32} className="text-muted-foreground" />
            </div>
            <h3 className="font-display text-xl font-semibold mb-2">Inga förfrågningar ännu</h3>
            <p className="text-muted-foreground">
              När någon fyller i formuläret för Företag & Event kommer det att dyka upp här.
            </p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {requests.map((request) => (
              <div key={request.id} className={`card-warm p-6 flex flex-col h-full relative transition-colors duration-300 ${request.status === 'handled' ? 'bg-secondary/30 opacity-75' : 'bg-white'}`}>
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-display text-lg font-semibold text-foreground flex items-center gap-2">
                      <Building2 size={18} className="text-primary" />
                      {request.company}
                    </h3>
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                      <Calendar size={12} />
                      {formatDate(request.createdAt)}
                    </p>
                  </div>
                  <div>
                    {request.status === 'handled' ? (
                      <div className="flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-100 px-3 py-1.5 rounded-full">
                        <CheckCircle2 size={14} />
                        Hanterad
                      </div>
                    ) : (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-xs h-8"
                        onClick={() => handleUpdateStatus(request.id, 'handled')}
                        disabled={updatingId === request.id}
                      >
                        {updatingId === request.id ? <Loader2 size={14} className="animate-spin mr-1" /> : <CheckCircle2 size={14} className="mr-1" />}
                        Markera hanterad
                      </Button>
                    )}
                  </div>
                </div>

                <div className="space-y-2 mb-4 text-sm">
                  <p className="flex items-center gap-2 text-foreground">
                    <User size={16} className="text-muted-foreground" />
                    {request.name}
                  </p>
                  <p className="flex items-center gap-2 text-foreground">
                    <Mail size={16} className="text-muted-foreground" />
                    <a href={`mailto:${request.email}`} className="text-primary hover:underline">
                      {request.email}
                    </a>
                  </p>
                  {request.phone && (
                    <p className="flex items-center gap-2 text-foreground">
                      <Phone size={16} className="text-muted-foreground" />
                      <a href={`tel:${request.phone}`} className="text-primary hover:underline">
                        {request.phone}
                      </a>
                    </p>
                  )}
                </div>

                <div className="mt-auto pt-4 border-t border-border">
                  <h4 className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider">
                    Meddelande
                  </h4>
                  <p className="text-sm text-foreground whitespace-pre-wrap flex-grow">
                    {request.message}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default CompanyRequests;
